import { useRequestLocation } from "../../../../../components/requests/RequestLocationContext";
import RequestLocationField from "../../../../../components/requests/RequestLocationField";
import React from "react";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import Tooltip from "@mui/material/Tooltip";
import Pill from "../helpers/Pill";
import AddIcon from "@mui/icons-material/Add";
import TextField from "@mui/material/TextField";
import {
  formatDateReadable,
  formatTimestampDateReadable,
  formatDate,
  text_to_num,
} from "../../utilities/helpers";
import IconButton from "@mui/material/IconButton";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import ArchiveOutlinedIcon from "@mui/icons-material/ArchiveOutlined";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import { Drawer } from "@mui/material";
import DrawerHeader from "../helpers/DrawerHeader";
import DrawerFooterContainerSmall from "../helpers/DrawerFooterContainerSmall";
import { PurpleSwitch, generateTimeOptions, filterOptions, get_t_str } from "../../../../../helpers/helpers";
import DatePickerDropdown from "../helpers/DatePickerDropdown";
import TimeOffMineDrawer from "./TimeOffMineDrawer";
import FormControl from "@mui/material/FormControl";
import Autocomplete from "@mui/material/Autocomplete";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import FormControlLabel from "@mui/material/FormControlLabel";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: 600,
  bgcolor: "background.paper",
  border: "2px solid #E2E8F0",
  borderRadius: "15px",
  boxShadow: 24,
  p: 4,
};

function TimeOffMineTable(props, ref) {
  const [isDrawerOpen, setDrawerOpen] = React.useState(false);
  const location = useRequestLocation(props, isDrawerOpen);
  const [open, setOpen] = React.useState(false);
  const handleOpen = () => setOpen(true);
  const handleClose = () => setOpen(false);
  const [showHistory, setShowHistory] = React.useState(false);

  const [dateStart, setDateStart] = React.useState(
    formatDate(location.minEffectiveDate)
  );
  const [dateEnd, setDateEnd] = React.useState(
    formatDate(location.minEffectiveDate)
  );
  const [isDateSelected, setIsDateSelected] = React.useState(false);
  const [requestTitle, setRequestTitle] = React.useState("");
  const [requestDescription, setRequestDescription] = React.useState("");
  const [requestDate, setRequestDate] = React.useState("");
  const [showEndDate, setShowEndDate] = React.useState(false);
  const [showTimeRange, setShowTimeRange] = React.useState(false);
  const [startTime, setStartTime] = React.useState("");
  const [endTime, setEndTime] = React.useState("");
  const [timeOffType, setTimeOffType] = React.useState("UNPAID");
  const effectiveMinDate =
    timeOffType === "SICK"
      ? formatDate(new Date())
      : formatDate(location.minEffectiveDate);

  React.useEffect(() => {
    if ((timeOffType === "PTO" && !location.canRequestPto) || (timeOffType === "SICK" && !location.canRequestSicktime)) setTimeOffType("UNPAID");
  }, [timeOffType, location.canRequestPto, location.canRequestSicktime]);

  const [validationError, setValidationError] = React.useState("");

  // Allow a parent to open the "Request Time Off" drawer (e.g. from a header
  // button) without rendering this component's own add button.
  React.useImperativeHandle(
    ref,
    () => ({
      openAddDrawer: () => setDrawerOpen(true),
    }),
    []
  );

  // New state for time off detail drawer
  const [isTimeOffDrawerOpen, setTimeOffDrawerOpen] = React.useState(false);
  const [selectedTimeOff, setSelectedTimeOff] = React.useState(null);

  function TimeRangeInput({
    startTime,
    endTime,
    onStartTimeChange,
    onEndTimeChange,
    startPlaceholder = "Start Time",
    endPlaceholder = "End Time",
    startQhnum,
  }) {
    return (
      <div className="mt-2 w-full flex items-center relative">
        <div className="w-[42%]">
          <FormControl fullWidth size="small">
            <Autocomplete
              id="start-time"
              disableClearable
              options={generateTimeOptions(startQhnum)}
              value={startTime}
              onChange={(event, newValue) => {
                onStartTimeChange(newValue);
              }}
              filterOptions={filterOptions}
              size="small"
              onKeyDown={(event) => {
                if (
                  event.key === "-" ||
                  event.key === "Enter" ||
                  event.key === "Tab"
                ) {
                  event.preventDefault();
                  const firstOption = filterOptions(
                    generateTimeOptions(startQhnum),
                    { inputValue: event.target.value }
                  )[0];

                  if (firstOption) {
                    onStartTimeChange(firstOption);
                  }
                }
              }}
              sx={{
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={startPlaceholder}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px !important",
                      height: "45px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                      backgroundColor: "white",
                    },
                  }}
                />
              )}
            />
          </FormControl>
        </div>
        <div className="text-slate-500 w-[16%] flex items-center justify-center">
          <ArrowForwardIcon />
        </div>
        <div className="w-[42%]">
          <FormControl fullWidth size="small">
            <Autocomplete
              id="end-time"
              disableClearable
              options={generateTimeOptions(startQhnum)}
              value={endTime}
              onChange={(event, newValue) => {
                onEndTimeChange(newValue);
              }}
              filterOptions={filterOptions}
              size="small"
              onKeyDown={(event) => {
                if (event.key === "Enter" || event.key === "Tab") {
                  event.preventDefault();
                  const firstOption = filterOptions(
                    generateTimeOptions(startQhnum),
                    { inputValue: event.target.value }
                  )[0];

                  if (firstOption) {
                    onEndTimeChange(firstOption);
                  }
                }
              }}
              sx={{
                "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#6d28d9",
                },
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  placeholder={endPlaceholder}
                  sx={{
                    "& .MuiOutlinedInput-root": {
                      borderRadius: "8px !important",
                      height: "45px",
                      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                        borderColor: "#6d28d9",
                      },
                      backgroundColor: "white",
                    },
                  }}
                />
              )}
            />
          </FormControl>
        </div>
      </div>
    );
  }

  React.useEffect(() => {
    setValidationError("");
  }, [
    dateStart,
    dateEnd,
    requestTitle,
    requestDescription,
    showEndDate,
    showTimeRange,
    startTime,
    endTime,
    timeOffType,
  ]);

  React.useEffect(() => {
    if (dateStart < effectiveMinDate) {
      setDateStart(effectiveMinDate);
    }
    if (showEndDate && dateEnd < effectiveMinDate) {
      setDateEnd(effectiveMinDate);
    }
  }, [dateEnd, dateStart, effectiveMinDate, showEndDate]);

  React.useEffect(() => {
    // Convert strings to Date objects for comparison
    const startDate = new Date(dateStart);
    const endDate = new Date(dateEnd);

    // If start date is after end date, update end date to match start date
    if (startDate > endDate) {
      setDateEnd(dateStart);
    }
  }, [dateStart]);

  const toggleDrawer = (open) => (event) => {
    if (
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }
    setDrawerOpen(open);
  };

  const handleTimeOffRowClick = (request, timeline) => {
    // Format the request info to match what the drawer expects
    const requestInfo = {
      id: request.uuid,
      title: request.title,
      startDate: request.date_lower_bound,
      endDate: request.date_upper_bound,
      note: request.note,
      managerNote: request.acting_manager_note,
      dateSubmitted: formatTimestampDateReadable(request.created_at, props.storeTimezone),
      status: request.status,
      startTime: request.start_qhnum,
      endTime: request.end_qhnum,
      time_off_type: request.time_off_type
    };

    setSelectedTimeOff({ requestInfo, timeline });
    setTimeOffDrawerOpen(true);
  };

  const handleSubmit = () => {
    if (!location.ready) return;
    if ((timeOffType === "PTO" && !location.canRequestPto) || (timeOffType === "SICK" && !location.canRequestSicktime)) return;
    /*
    store_id = request.data.get('store_id')
    title = request.data.get('title')
    note = request.data.get('note')
    date_lower_bound = request.data.get('date_lower_bound')
    date_upper_bound = request.data.get('date_upper_bound')
    */

    if (requestTitle.trim() === "") {
      setValidationError("Must include title to submit time off request.");
      return;
    }

    // Get conflicting blackout dates
    const conflictingDates = isDateInBlackout(dateStart, dateEnd);

    if (conflictingDates) {
      setValidationError(
        `The selected date range conflicts with blackout dates: ${conflictingDates.join(
          ", "
        )}. Please choose a different date range.`
      );
      return;
    }
    let startNum;
    let endNum;
    if (showTimeRange) {
      if (!startTime || !endTime) {
        setValidationError('Start and end times are required.');
        return;
      }
      startNum = text_to_num(startTime);
      endNum = text_to_num(endTime);
      if (startNum >= endNum) {
        setValidationError('Start time must be before end time.');
        return;
      }
    }

    let data = {
      title: requestTitle,
      note: requestDescription,
      date_lower_bound: dateStart,
      time_off_type: timeOffType,
    };
    if (showEndDate) {
      data["date_upper_bound"] = dateEnd;
    }
    if (showTimeRange) {
      data.start_qhnum = startNum;
      data.end_qhnum = endNum;
    }
    props.request_time_off(location.payload(data));
    handleClose();
    setDrawerOpen(false);
    // Reset form fields
    setRequestTitle("");
    setRequestDescription("");
    setDateStart(formatDate(location.minEffectiveDate));
    setDateEnd(formatDate(location.minEffectiveDate));
    setShowEndDate(false);
    setShowTimeRange(false);
    setStartTime("");
    setEndTime("");
    setTimeOffType("UNPAID");
    setValidationError("");
  };

  const hasTimeOffRequests = props.myTimeOffData.length > 0;

  // For the start date datepicker
  const [showDailyDatePicker, setShowDailyDatePicker] = React.useState(false);
  const datePickerRef = React.useRef(null);

  function toggleDailyDatePicker() {
    setShowDailyDatePicker((prevState) => !prevState);
  }

  const timeOffRequests = props.myTimeOffData;

  // Helper function to categorize time off requests into "Upcoming" and "History"
  const categorizeTimeOffRequests = (requests) => {
    const currentDate = new Date();
    return requests.reduce(
      (acc, request) => {
        const startDate = new Date(request.date_lower_bound);
        if (startDate > currentDate) {
          acc.upcoming.push(request);
        } else {
          acc.history.push(request);
        }
        return acc;
      },
      { upcoming: [], history: [] }
    );
  };

  const [categorizedTimeOffRequests, setCategorizedTimeOffRequests] =
    React.useState(categorizeTimeOffRequests(timeOffRequests));

  React.useEffect(() => {
    setCategorizedTimeOffRequests(categorizeTimeOffRequests(timeOffRequests));
  }, [props.myTimeOffData]);

  // console.log("TIME OFF", categorizedTimeOffRequests.upcoming)

  React.useEffect(() => {
    // This function checks if the clicked target is inside the date picker component
    function handleClickOutside(event) {
      if (
        datePickerRef.current &&
        !datePickerRef.current.contains(event.target)
      ) {
        setShowDailyDatePicker(false);
      }
    }

    // Add the event listener to the document when the component is mounted
    if (showDailyDatePicker) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    // Clean up the event listener when the component is unmounted or the effect re-runs
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showDailyDatePicker]); // Re-run the effect when 'showDailyDatePicker' changes

  // For the end date datepicker
  const [showDailyDatePicker2, setShowDailyDatePicker2] = React.useState(false);
  const datePickerRef2 = React.useRef(null);

  function toggleDailyDatePicker2() {
    setShowDailyDatePicker2((prevState) => !prevState);
  }

  React.useEffect(() => {
    // This function checks if the clicked target is inside the date picker component
    function handleClickOutside2(event) {
      if (
        datePickerRef2.current &&
        !datePickerRef2.current.contains(event.target)
      ) {
        setShowDailyDatePicker2(false);
      }
    }

    // Add the event listener to the document when the component is mounted
    if (showDailyDatePicker2) {
      document.addEventListener("mousedown", handleClickOutside2);
    }

    // Clean up the event listener when the component is unmounted or the effect re-runs
    return () => {
      document.removeEventListener("mousedown", handleClickOutside2);
    };
  }, [showDailyDatePicker2]); // Re-run the effect when 'showDailyDatePicker' changes

  // console.log("DATE START", dateStart)
  // console.log("DATE END", dateEnd)

  const handleDateStartChange = (newDate) => {
    setDateStart(newDate);
    // If end date is before new start date, update end date
    if (new Date(newDate) > new Date(dateEnd)) {
      setDateEnd(newDate);
    }
  };

  const isDateInBlackout = (startDate, endDate) => {
    const blackoutDates = location.blackoutDates.map((item) => item.date);

    // Parse date strings (YYYY-MM-DD) to local Date objects
    // dateStart and dateEnd are strings, not Date objects
    const parseLocalDate = (dateStr) => {
      const [year, month, day] = dateStr.split('-').map(Number);
      return new Date(year, month - 1, day, 0, 0, 0, 0);
    };

    const start = parseLocalDate(startDate);
    const end = parseLocalDate(endDate);

    const conflictingDates = [];

    // Iterate through all dates in the selected range
    const current = new Date(start);
    while (current <= end) {
      const formattedDate = formatDate(current); // Format to "YYYY-MM-DD"
      if (blackoutDates.includes(formattedDate)) {
        conflictingDates.push(formattedDate);
      }
      current.setDate(current.getDate() + 1);
    }

    // Return conflicting dates or null if no conflicts
    return conflictingDates.length > 0 ? conflictingDates : null;
  };

  return (
    <>
      <div className="mt-12">
        {hasTimeOffRequests ? (
          <div>
            <div>
              {!props.hideHeader && (
                <div className="w-full flex items-center justify-between px-6">
                  <h1 className="text-xl font-bold text-slate-900">Time Off</h1>
                  <button
                    className="bg-violet-50 border border-violet-200 hover:bg-violet-100 text-violet-700 h-9 w-9 rounded-lg flex items-center justify-center transition-colors"
                    onClick={toggleDrawer(true)}
                  >
                    <AddIcon sx={{ fontSize: 18 }} />
                  </button>
                </div>
              )}
              <div className="mt-8 px-6">
                <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">Upcoming</p>
              </div>
              <div className="mt-3 flex flex-col">
                <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 min-w-full">
                  <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                    <div className="overflow-hidden">
                      <table className="min-w-full table-fixed divide-y divide-slate-100">
                        <thead>
                          <tr>
                            <th
                              scope="col"
                              className="w-[30%] py-3 pl-6 pr-3 text-left text-xs font-semibold text-slate-500"
                            >
                              Title
                            </th>
                            <th
                              scope="col"
                              className="w-[15%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                            >
                              Type
                            </th>
                            <th
                              scope="col"
                              className="w-[25%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                            >
                              Period
                            </th>
                            <th
                              scope="col"
                              className="w-1/12 relative py-3 pl-3 pr-6"
                            >
                              <span className="sr-only">Actions</span>
                            </th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-slate-100 bg-white">
                          {categorizedTimeOffRequests.upcoming.map(
                            (request, index) => (
                              <tr
                                key={index}
                                className="hover:bg-slate-50 cursor-pointer transition-colors"
                                onClick={() => handleTimeOffRowClick(request, "Upcoming")}
                              >
                                <td className="py-3.5 pl-6 pr-3 text-sm max-w-0">
                                  <div className="font-semibold text-slate-900 truncate">
                                    {request.title}
                                  </div>
                                  {request.note && (
                                    <div className="text-xs text-slate-400 truncate mt-0.5">
                                      {request.note}
                                    </div>
                                  )}
                                </td>
                                <td className="whitespace-nowrap px-3 py-3.5 text-sm text-slate-500">
                                  {request.time_off_type ? (
                                    request.time_off_type === 'UNPAID' ? 'Unpaid' :
                                    request.time_off_type === 'PTO' ? 'PTO' :
                                    request.time_off_type === 'SICK' ? 'Sick' :
                                    'Not specified'
                                  ) : (
                                    <span className="text-slate-400">&mdash;</span>
                                  )}
                                </td>
                                <td className="whitespace-nowrap px-3 py-3.5 text-sm text-slate-500">
                                  <div>
                                    <span>
                                      {formatDateReadable(request.date_lower_bound)}
                                    </span>
                                    {request.date_upper_bound && (
                                      <span>
                                        {" "}&mdash;{" "}
                                        {formatDateReadable(request.date_upper_bound)}
                                      </span>
                                    )}
                                  </div>
                                  {(request.start_qhnum && request.end_qhnum) &&
                                    <div className="text-slate-400 text-xs mt-0.5">
                                      {get_t_str(request.start_qhnum)} &mdash; {get_t_str(request.end_qhnum)}
                                    </div>
                                  }
                                </td>
                                <td className="relative whitespace-nowrap py-3.5 pl-3 pr-6 text-right text-sm font-medium">
                                  <div className="w-full flex items-center justify-end gap-x-3">
                                    {request.acting_manager_note && (
                                      <Tooltip title={request.acting_manager_note}>
                                        <span className="text-violet-600 cursor-pointer">
                                          <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                                        </span>
                                      </Tooltip>
                                    )}
                                    <Pill status={request.status.toLowerCase()} />
                                    {props.archive_time_off_request && (
                                      <div className="w-10 flex items-center justify-center">
                                        {request.status.toLowerCase() !== "approved" &&
                                          request.status.toLowerCase() !== "pending" ? (
                                            <Tooltip title="Archive">
                                              <IconButton
                                                className="text-slate-400"
                                                sx={{ backgroundColor: "#f8fafc", "&:hover": { backgroundColor: "#f1f5f9" } }}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  props.archive_time_off_request(request.uuid);
                                                }}
                                              >
                                                <ArchiveOutlinedIcon sx={{ fontSize: 18 }} />
                                              </IconButton>
                                            </Tooltip>
                                          ) : null}
                                      </div>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                      <div className="border-b border-slate-100"></div>
                      {categorizedTimeOffRequests.upcoming?.length === 0 && (
                        <div className="w-full flex flex-col items-center justify-center mt-10 mb-4">
                          <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                            <InfoOutlinedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                          </div>
                          <p className="mt-3 text-sm text-slate-400">
                            You have no upcoming time off requests
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {!showHistory && categorizedTimeOffRequests.history.length > 0 && (
              <div className="px-6 mt-8">
                <button
                  className="w-full py-3 flex items-center justify-center rounded-xl border border-slate-200 hover:bg-slate-50 transition-colors"
                  onClick={() => setShowHistory(true)}
                >
                  <span className="text-sm text-slate-500">Show history ({categorizedTimeOffRequests.history.length})</span>
                </button>
              </div>
            )}

            {showHistory && (
              <div>
                <div className="mt-10 px-6">
                  <p className="text-xs font-semibold text-slate-500 uppercase tracking-wider">History</p>
                </div>
                <div className="mt-3 flex flex-col">
                  <div className="-my-2 overflow-x-auto sm:-mx-6 lg:-mx-8 min-w-full">
                    <div className="inline-block min-w-full py-2 align-middle md:px-6 lg:px-8">
                      <div className="overflow-hidden">
                        <table className="min-w-full table-fixed divide-y divide-slate-100">
                          <thead>
                            <tr>
                              <th
                                scope="col"
                                className="w-[30%] py-3 pl-6 pr-3 text-left text-xs font-semibold text-slate-500"
                              >
                                Title
                              </th>
                              <th
                                scope="col"
                                className="w-[15%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                              >
                                Type
                              </th>
                              <th
                                scope="col"
                                className="w-[25%] px-3 py-3 text-left text-xs font-semibold text-slate-500"
                              >
                                Period
                              </th>
                              <th
                                scope="col"
                                className="w-1/12 relative py-3 pl-3 pr-6"
                              >
                                <span className="sr-only">Actions</span>
                              </th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-slate-100 bg-white">
                            {categorizedTimeOffRequests.history.map(
                              (request, index) => (
                                <tr
                                  key={index}
                                  className="hover:bg-slate-50 cursor-pointer transition-colors"
                                  onClick={() => handleTimeOffRowClick(request, "History")}
                                >
                                  <td className="py-3.5 pl-6 pr-3 text-sm max-w-0">
                                    <div className="font-semibold text-slate-900 truncate">
                                      {request.title}
                                    </div>
                                    {request.note && (
                                      <div className="text-xs text-slate-400 truncate mt-0.5">
                                        {request.note}
                                      </div>
                                    )}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-3.5 text-sm text-slate-500">
                                    {request.time_off_type ? (
                                      request.time_off_type === 'UNPAID' ? 'Unpaid' :
                                      request.time_off_type === 'PTO' ? 'PTO' :
                                      request.time_off_type === 'SICK' ? 'Sick' :
                                      'Not specified'
                                    ) : (
                                      <span className="text-slate-400">&mdash;</span>
                                    )}
                                  </td>
                                  <td className="whitespace-nowrap px-3 py-3.5 text-sm text-slate-500">
                                    <div>
                                      <span>
                                        {formatDateReadable(request.date_lower_bound)}
                                      </span>
                                      {request.date_upper_bound && (
                                        <span>
                                          {" "}&mdash;{" "}
                                          {formatDateReadable(request.date_upper_bound)}
                                        </span>
                                      )}
                                    </div>
                                    {(request.start_qhnum && request.end_qhnum) &&
                                      <div className="text-slate-400 text-xs mt-0.5">
                                        {get_t_str(request.start_qhnum)} &mdash; {get_t_str(request.end_qhnum)}
                                      </div>
                                    }
                                  </td>
                                  <td className="relative whitespace-nowrap py-3.5 pl-3 pr-6 text-right text-sm font-medium">
                                    <div className="w-full flex items-center justify-end gap-x-3">
                                      {request.acting_manager_note && (
                                        <Tooltip title={request.acting_manager_note}>
                                          <span className="text-violet-600 cursor-pointer">
                                            <ChatBubbleOutlineIcon sx={{ fontSize: 16 }} />
                                          </span>
                                        </Tooltip>
                                      )}
                                      <Pill status={request.status.toLowerCase()} />
                                      {props.archive_time_off_request && (
                                        <Tooltip title="Archive">
                                          <IconButton
                                            className="text-slate-400"
                                            sx={{ backgroundColor: "#f8fafc", "&:hover": { backgroundColor: "#f1f5f9" } }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              props.archive_time_off_request(request.uuid);
                                            }}
                                          >
                                            <ArchiveOutlinedIcon sx={{ fontSize: 18 }} />
                                          </IconButton>
                                        </Tooltip>
                                      )}
                                    </div>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                        <div className="border-b border-slate-100"></div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="w-full">
            {!props.hideHeader && (
              <div className="w-full flex items-center justify-between px-6">
                <h1 className="text-xl font-bold text-slate-900">Time Off</h1>
                <button
                  className="bg-violet-50 border border-violet-200 hover:bg-violet-100 text-violet-700 h-9 w-9 rounded-lg flex items-center justify-center transition-colors"
                  onClick={toggleDrawer(true)}
                >
                  <AddIcon sx={{ fontSize: 18 }} />
                </button>
              </div>
            )}
            <div className="w-full h-72 border-t border-slate-100 mt-8 flex items-center justify-center">
              <div className="w-full flex flex-col items-center justify-center">
                <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center">
                  <InfoOutlinedIcon sx={{ fontSize: 20, color: "#94a3b8" }} />
                </div>
                <p className="mt-3 text-sm text-slate-400">
                  You have no time off requests at the moment
                </p>
              </div>
            </div>
          </div>
        )}
        <Drawer anchor="right" open={isDrawerOpen} onClose={toggleDrawer(false)}>
          <div className="w-[450px] max-w-[100vw] h-screen bg-white relative">
            <div className="max-h-screen overflow-auto">
              <DrawerHeader
                title="Request Time Off"
                onClose={toggleDrawer(false)}
              />
              <div className="px-6 py-5">
                <RequestLocationField location={location} />
                <div className="mb-5 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3">
                  <p className="text-sm text-slate-600">
                    If approved, this time off will appear at all locations where you are on the team.
                  </p>
                </div>
                <div>
                  <h3 className="text-slate-500 text-sm">Title *</h3>
                  <div className="mt-1">
                    <TextField
                      fullWidth
                      id="outlined-basic"
                      variant="outlined"
                      placeholder="e.g. Vacation request"
                      size="small"
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "12px",
                          paddingY: "5px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                      value={requestTitle}
                      onChange={(event) => setRequestTitle(event.target.value)}
                    />
                  </div>
                </div>
                <div className="mt-5">
                  <h3 className="text-slate-500 text-sm">Note</h3>
                  <div className="mt-1">
                    <TextField
                      fullWidth
                      id="outlined-multiline-static"
                      multiline
                      rows={3}
                      variant="outlined"
                      placeholder="Leave a note (optional)"
                      size="small"
                      InputProps={{
                        sx: {
                          backgroundColor: "white",
                          borderRadius: "12px",
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                            borderColor: "#6d28d9",
                          },
                        },
                      }}
                      value={requestDescription}
                      onChange={(event) =>
                        setRequestDescription(event.target.value)
                      }
                    />
                  </div>
                </div>
                {validationError && (
                  <div className="mt-5 bg-rose-50 border border-rose-200 rounded-xl px-4 py-3 flex items-start space-x-2">
                    <WarningAmberRoundedIcon sx={{ fontSize: 18, color: "#e11d48" }} className="mt-0.5" />
                    <span className="text-sm text-rose-700">{validationError}</span>
                  </div>
                )}
                <div className="border-t border-slate-100 mt-5"></div>
                {(location.canRequestPto || location.canRequestSicktime) && (
                  <div className="mt-5">
                    <h3 className="text-slate-500 text-sm">Type</h3>
                    <div>
                      <FormControl component="fieldset">
                        <RadioGroup
                          value={timeOffType}
                          onChange={(event) => setTimeOffType(event.target.value)}
                        >
                          <FormControlLabel
                            value="UNPAID"
                            control={<Radio sx={{ '&.Mui-checked': { color: '#6d28d9' } }} />}
                            label="Unpaid"
                          />
                          {location.canRequestPto && (
                            <FormControlLabel
                              value="PTO"
                              control={<Radio sx={{ '&.Mui-checked': { color: '#6d28d9' } }} />}
                              label="Paid Time Off (PTO)"
                            />
                          )}
                          {location.canRequestSicktime && (
                            <FormControlLabel
                              value="SICK"
                              control={<Radio sx={{ '&.Mui-checked': { color: '#6d28d9' } }} />}
                              label="Sick Leave"
                            />
                          )}
                        </RadioGroup>
                      </FormControl>
                    </div>
                  </div>
                )}
                <div className="mt-5">
                  <h3 className="text-slate-500 text-sm">
                    {showEndDate ? "Start Date" : "Date"}
                  </h3>
                  <div className="mt-1">
                    <DatePickerDropdown
                      showDailyDatePicker={showDailyDatePicker}
                      toggleDailyDatePicker={toggleDailyDatePicker}
                      datePickerRef={datePickerRef}
                      setDate={handleDateStartChange}
                      selectedDate={dateStart}
                      minEffectiveDate={effectiveMinDate}
                      blackoutDates={location.blackoutDates}
                    />
                  </div>
                </div>
                {showEndDate && (
                  <div className="mt-5">
                    <p className="text-slate-500 text-sm">End Date</p>
                    <div className="mt-1">
                      <DatePickerDropdown
                        showDailyDatePicker={showDailyDatePicker2}
                        toggleDailyDatePicker={toggleDailyDatePicker2}
                        datePickerRef={datePickerRef2}
                        setDate={setDateEnd}
                        selectedDate={dateEnd}
                        minEffectiveDate={dateStart}
                        blackoutDates={location.blackoutDates}
                      />
                    </div>
                  </div>
                )}
                <div className="mt-5 flex items-center">
                  <PurpleSwitch
                    checked={showEndDate}
                    onChange={() => setShowEndDate(!showEndDate)}
                  />
                  <p className="text-slate-500 text-sm">
                    Request more than one day off
                  </p>
                </div>
                <div className="mt-5 flex items-center">
                  <PurpleSwitch
                    checked={showTimeRange}
                    onChange={() => setShowTimeRange(!showTimeRange)}
                  />
                  <p className="text-slate-500 text-sm">
                    Request only part of the day off
                  </p>
                </div>
                {showTimeRange && (
                  <TimeRangeInput
                    startTime={startTime}
                    endTime={endTime}
                    onStartTimeChange={setStartTime}
                    onEndTimeChange={setEndTime}
                    startPlaceholder="Start Time"
                    endPlaceholder="End Time"
                    startQhnum={0}
                  />
                )}
              </div>
              <div className="h-[200px]"></div>
            </div>
            <DrawerFooterContainerSmall>
              <button type="button" disabled={!location.ready} onClick={handleSubmit}
                className="w-full py-3 rounded-xl bg-violet-700 hover:bg-violet-800 text-white text-lg disabled:bg-slate-200 disabled:text-slate-500 disabled:cursor-not-allowed">
                {location.loading ? "Loading location…" : "Request"}
              </button>
            </DrawerFooterContainerSmall>
          </div>
        </Drawer>
      </div>
      {selectedTimeOff && (
        <TimeOffMineDrawer
          isDrawerOpen={isTimeOffDrawerOpen}
          setDrawerOpen={setTimeOffDrawerOpen}
          requestInfo={selectedTimeOff.requestInfo}
          timeline={selectedTimeOff.timeline}
          rescind_time_off_request={props.rescind_time_off_request}
        />
      )}
    </>
  );
}

export default React.forwardRef(TimeOffMineTable);
